<template>
    <v-card
        class="fill-height"
        :class="{ 'in-modal': inModal }"
        flat
    >
        <Form
            ref="elFormObj"
            as=""
            v-slot="{ errors: formErrors, meta: formMeta, resetForm }"
        >
            <Dimmer
                :hide-spinner="false"
                :model-value="progress"
            >
                <template #content>
                    <form
                        @submit="onSubmit($event)"
                        @reset.prevent="resetForm()"
                        novalidate
                    >

                        <v-card-title
                            class="d-block pa-0"
                        >
                            <v-toolbar
                                color="secondary"
                                density="compact"
                                flat
                            >
                                <v-toolbar-title
                                    v-if="userBody"
                                    class="text-title-large"
                                >
                                    Edit Body
                                </v-toolbar-title>
                                <v-toolbar-title
                                    v-else
                                    class="text-title-large"
                                >
                                    Add New Body
                                </v-toolbar-title>
                                <v-btn
                                    v-if="inModal"
                                    @click="exit()"
                                    :icon="mdiWindowClose"
                                >
                                </v-btn>
                            </v-toolbar>
                        </v-card-title>
                        <v-card-text
                            v-if="lo_isError(userBody)"
                            class="pa-0"
                        >
                            <v-alert
                                tile
                                type="error"
                            >
                                Unknown error. Please reload this page and try again.
                            </v-alert>
                        </v-card-text>
                        <v-card-text
                            class="pa-0"
                        >
                            <DebugContent>
                                <template #default='{ bgCssClass }'>
                                    <v-container :class="bgCssClass">
                                        <details>
                                            <summary class="cursor-pointer">Errors: {{ elFormObj?.errors?.length || 0 }}</summary>
                                            <pre>{{ elFormObj?.errors || 'none' }}</pre>
                                        </details>
                                    </v-container>
                                </template>
                            </DebugContent>

                            <v-container>
                                <v-row>
                                    <v-col>
                                        <p>
                                            We predict body dimensions using an algorithm trained on over 25,000 human
                                            body scans. The answers to these questions are
                                            <strong>only</strong>
                                            used to predict body dimensions, which garments are then optimized to fit.
                                        </p>
                                    </v-col>
                                </v-row>

                                <!-- Name -->
                                <v-row>
                                    <v-col>
                                        <div class="d-flex">
                                            <label class="d-block font-weight-bold">Name</label>
                                        </div>
                                        <Field
                                            name="name"
                                            label="Name"
                                            v-slot="{ field, errors }"
                                            v-model="form.name"
                                            rules="required"
                                        >
                                            <v-text-field
                                                v-bind="field"
                                                :model-value="field.value"
                                                :error-messages="errors"
                                                name="name"
                                                label=""
                                                variant="outlined"
                                                required
                                            ></v-text-field>
                                        </Field>
                                    </v-col>
                                </v-row>

                                <!-- Measurements (metric / imperial) -->
                                <v-row>
                                    <v-col>
                                        <v-btn-toggle
                                            v-model="measuringSystem"
                                            class="fill-width"
                                            variant="outlined"
                                            density="compact"
                                            divided
                                            mandatory
                                            required
                                        >
                                            <v-btn value="imperial" class="flex-grow-1">US</v-btn>
                                            <v-btn value="metric" class="flex-grow-1">Metric</v-btn>
                                        </v-btn-toggle>
                                    </v-col>
                                </v-row>

                                <!-- Height -->
                                <v-row>
                                    <v-col>
                                        <div class="d-flex">
                                            <label class="d-block font-weight-bold">
                                                Height <span class="font-weight-regular">({{ heightSymbol }})</span>
                                            </label>
                                        </div>
                                        <Field
                                            name="height"
                                            label="Height"
                                            v-slot="{ field, errors }"
                                            v-model="form.height"
                                            rules="required"
                                        >
                                            <BodyHeightInput
                                                v-bind="field"
                                                :model-value="(field.value as Nilish<number>)"
                                                :system="measuringSystem"
                                            ></BodyHeightInput>
                                        </Field>
                                    </v-col>
                                </v-row>

                                <!-- Weight -->
                                <v-row>
                                    <v-col>
                                        <div class="d-flex">
                                            <label class="d-block font-weight-bold">
                                                Weight <span class="font-weight-regular">({{ weightSymbol }})</span>
                                            </label>
                                        </div>
                                        <Field
                                            name="weight"
                                            label="Weight"
                                            v-slot="{ field, errors }"
                                            v-model="form.weight"
                                            rules="required"
                                        >
                                            <BodyWeightInput
                                                v-bind="field"
                                                :model-value="(field.value as Nilable<number>)"
                                                :system="measuringSystem"
                                            ></BodyWeightInput>
                                        </Field>
                                    </v-col>
                                </v-row>

                                <!-- Date of Birth -->
                                <v-row>
                                    <v-col>
                                        <div class="d-flex">
                                            <label class="d-block font-weight-bold" rules="required">Birthdate</label>
                                        </div>
                                        <Field
                                            name="birthdate"
                                            label="Birthdate"
                                            v-slot="{ field, errors }"
                                            v-model="form.birthdate"
                                            rules="required"
                                        >
                                            <BirhtdateInput
                                                v-bind="field"
                                                :model-value="(field.value as Nullable<string>)"
                                                label="Birthdate"
                                                required
                                            ></BirhtdateInput>
                                        </Field>
                                    </v-col>
                                </v-row>

                                <!-- Gender -->
                                <v-row>
                                    <v-col>
                                        <div class="d-flex">
                                            <label class="d-block font-weight-bold">Gender</label>
                                            <v-spacer />
                                            <v-chip color="secondary" size="x-small" label>OPTIONAL</v-chip>
                                        </div>

                                        <GenderSelectorInput
                                            v-model="form.gender"
                                            class="fill-width"
                                            mandatory
                                        ></GenderSelectorInput>
                                    </v-col>
                                </v-row>

                                <!-- Bra Size -->
                                <v-row v-if="form.gender === 'F'">
                                    <v-col>
                                        <div class="d-flex">
                                            <label class="d-block font-weight-bold">Bra Size</label>
                                            <v-spacer />
                                            <v-chip color="secondary" size="x-small" label>OPTIONAL</v-chip>
                                        </div>
                                        <Field
                                            name="braSize"
                                            label="Bra Size"
                                            v-slot="{ field, errors }"
                                            v-model="form.braSize"
                                        >
                                            <BraSizeInput
                                                v-bind="field"
                                                :model-value="(field.value as Nilish<string>)"
                                                label="Bra Size"
                                            ></BraSizeInput>
                                        </Field>
                                    </v-col>
                                </v-row>

                                <!-- Male Body Type -->
                                <v-row v-if="form.gender === 'M'">
                                    <v-col>
                                        <div class="d-flex">
                                            <label class="d-block font-weight-bold">Body Type</label>
                                            <v-spacer />
                                            <v-chip color="secondary" size="x-small" label>OPTIONAL</v-chip>
                                        </div>

                                        <Field
                                            name="maleBodyType1"
                                            label="Body Type"
                                            v-slot="{ field, errors }"
                                            v-model="form.maleBodyType1"
                                        >
                                            <BodyTypeInput
                                                v-bind="field"
                                                :model-value="(field.value as Nilish<number>)"
                                                label="Body Type"
                                            ></BodyTypeInput>
                                        </Field>
                                    </v-col>
                                </v-row>

                                <!-- Ethnicity -->
                                <v-row>
                                    <v-col>
                                        <div class="d-flex">
                                            <label class="d-block font-weight-bold">Ethnicity</label>
                                            <v-spacer />
                                            <v-chip color="secondary" size="x-small" label>OPTIONAL</v-chip>
                                        </div>

                                        <EthnicityInput
                                            v-model="form.ethnicity"
                                            label="Ethnicity"
                                        ></EthnicityInput>
                                    </v-col>
                                </v-row>

                            </v-container>
                        </v-card-text>

                        <v-card-actions>
                            <v-spacer></v-spacer>
                            <v-btn
                                variant="text"
                                color="secondary"
                                size="large"
                                @click="exit()"
                            >
                                Cancel
                            </v-btn>
                            <v-btn
                                variant="flat"
                                class="mr-4"
                                color="primary"
                                type="submit"
                                size="large"
                                :disabled="!formMeta.valid"
                            >
                                Save
                            </v-btn>
                        </v-card-actions>
                    </form>
                </template>
            </Dimmer>
        </Form>
    </v-card>
</template>

<script lang="ts" setup>
    import type { Field, Form } from 'vee-validate';
    import { BodiesModel } from '~/models';
    import { UserBodyMale, type UserBody } from '~/types';
    import type { BaseModel } from '~/models';
    import Dimmer from 'x1-base/components/Dimmer/Dimmer.vue';
    import { isError as lo_isError } from 'es-toolkit';
    import { isNil as lo_isNil } from 'es-toolkit';

    import BodyHeightInput from '~/components/controls/BodyHeightInput/BodyHeightInput.vue';
    import BodyWeightInput from '~/components/controls/BodyWeightInput/BodyWeightInput.vue';

    import BirhtdateInput from '~/components/controls/BirthdateInput/BirthdateInput.vue';
    import EthnicityInput from '~/components/controls/EthnicityInput/EthnicityInput.vue';
    import BodyTypeInput from '~/components/controls/BodyTypeInput/BodyTypeInput.vue';
    import BraSizeInput from '~/components/controls/BraSizeInput/BraSizeInput.vue';
    import GenderSelectorInput from '~/components/controls/GenderSelectorInput/GenderSelectorInput.vue';
    import { mdiWindowClose } from '@mdi/js';
    import { initVeeRules } from '~/helpers/forms';

    initVeeRules([ 'required' ]);

    const props = defineProps({
        userBody: {
            type: [Object, Error] as PropType<Nilish<BaseModel<UserBody> | BodiesModel | UserBody | Error>>,
            default: () => {}
        },
        inModal: {
            type: Boolean,
            default: false
        },
        progress: {
            type: Boolean,
            default: false
        }
    });

    const emit = defineEmits([
        'on-save',
        'on-exit'
    ]);

    const elFormObj = shallowRef<InstanceType<typeof Form>>();
    const form = ref<Partial<UserBody>>(BodiesModel.toPlainObject<UserBody>(props.userBody));

    const measuringSystem = ref<string>('imperial');
    const weightSymbol = computed(() => {
        return measuringSystem.value === 'imperial' ? 'lbs' : 'kg';
    });
    const heightSymbol = computed(() => {
        return measuringSystem.value === 'imperial' ? 'ft/in' : 'mm';
    });

    const unWatchBody = watch(
        () => props.userBody,
        (newVal) => {
            form.value = BodiesModel.toPlainObject<Partial<UserBody>>(newVal);
        }
    );

    async function onSubmit(e: Event): Promise<void> {
        e.preventDefault();

        const validateObj = await elFormObj.value?.validate();

        if (!validateObj?.valid) {
            return;
        }

        const bodyObj = form.value ? BodiesModel.toPlainObject<Nullable<UserBody>>(form.value) : null;

        // Data Corrections

        if (bodyObj && 'ethnicity' in bodyObj && lo_isNil(bodyObj.ethnicity)) {
            delete bodyObj?.ethnicity;
        }

        if (bodyObj && !(`${bodyObj.maleBodyType1}` in UserBodyMale)) {
            bodyObj.maleBodyType1 = null;
        }

        // Send
        emit('on-save', bodyObj);
    }

    function exit(): void {
        emit('on-exit');
    }

    onBeforeUnmount(() => {
        unWatchBody();
    });
</script>

<style lang="scss" scoped></style>
